import axios from 'axios';
import tokenStore from './tokenStore';
import { refreshTokens, ensureFreshToken } from 'services/idAuth';

// Bearer header from the token obtained via the id code exchange (or {} if none).
const authHeader = () => {
    const token = tokenStore.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const getJSON = (url, options) => {
    const { useAuth } = Object.assign({}, { useAuth: true }, options);
    const doFetch = () => fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(useAuth ? authHeader() : {}),
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
    });
    return (async () => {
        // Pre-hook: renew a missing/expiring access token before firing, so the
        // request carries a valid token instead of paying a 401 round-trip.
        if (useAuth) await ensureFreshToken();
        const response = await doFetch();
        // Fallback: token rejected anyway (e.g. revoked server-side) -> one
        // silent refresh, then retry.
        if (response.status === 401 && useAuth && await refreshTokens()) {
            return doFetch().then(r => r.json());
        }
        return response.json();
    })();
};

const postJSON = (url, body, options) => {
    const { useAuth } = Object.assign({}, { useAuth: true }, options);
    const doFetch = () => fetch(url, {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(useAuth ? authHeader() : {}),
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: body,
    });
    return (async () => {
        // Pre-hook: renew a missing/expiring access token before firing.
        if (useAuth) await ensureFreshToken();
        const response = await doFetch();
        // A 401 is rejected before the request body is processed, so retrying
        // after a silent refresh is side-effect free.
        if (response.status === 401 && useAuth && await refreshTokens()) {
            return doFetch().then(r => r.json());
        }
        return response.json();
    })();
};


// axios instance for making requests
const axiosInstance = axios.create();

// request interceptor for adding token
axiosInstance.interceptors.request.use(async (config) => {
    // Pre-hook: renew a missing/expiring access token before the request goes
    // out, so an async call on an already-open page doesn't rely on the 401
    // response interceptor below (which stays as a fallback).
    await ensureFreshToken();
    const token = tokenStore.getAccessToken();
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.paramsSerializer = params => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          if (Array.isArray(params[key])) {
            params[key].forEach(element => {
              searchParams.append(key, element)
            });
          }
          else {
            searchParams.append(key, params[key]);
          }
        });
        return searchParams.toString();
      };
    return config;
});

// response interceptor: on 401, attempt one silent refresh and replay the request.
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && original && !original._retried) {
            original._retried = true;
            if (await refreshTokens()) {
                original.headers = {
                    ...original.headers,
                    Authorization: `Bearer ${tokenStore.getAccessToken()}`,
                };
                return axiosInstance(original);
            }
        }
        return Promise.reject(error);
    }
);

//export default axiosInstance;


export { getJSON, postJSON, axiosInstance };
